/**
 * The Character panel (ADR-017) — per-avatar properties. Two tenants:
 * arm-space (constant abduction, a display filter) and head-space (constant
 * cervical pitch, the ADR-019 §10 seating property — since ADR-030 a BIND
 * property of the armature: the exported character carries it). Both commit
 * through the command log (`armSpace.set` / `headSpace.set`, undoable like
 * every edit), rendered from the store on every notify so undo/redo and a
 * loaded Document move the knobs and never just the bones.
 *
 * r2 (SHELL_CHROME_SPEC §12–§13): each tenant is a VALUE ROW — slider +
 * numeric entry, two doors into one commit — with its explanation in the
 * tooltip tiers instead of ambient paragraphs. Evidence stays on the
 * surface: a refusal renders its reason line, and head-space wears a
 * provenance chip (`baseline` = the measured seating default is live,
 * `set` = a person overrode it) because a measurement dressed as a
 * preference would fail the §1.1 betrayal test.
 *
 * The capability question is asked at render time, never captured: the
 * subject is replaceable, and a rig that cannot carry a property DISABLES
 * its row showing the refusal's own reason (ADR-017 §7).
 * Depends on: panel-registry (types), value-row, kernel (the band constants
 * — a control cannot express a value the gates would refuse). Depended on
 * by: main (registration site).
 */
import { ARM_SPACE_MAX_DEGREES, HEAD_SPACE_MAX_DEGREES, type HeadSeatingMarker } from '@harryhausen/kernel';
import type { PanelMount } from './panel-registry.js';
import { createValueRow } from './value-row.js';

export type ArmSpaceUiState =
  | {
      kind: 'ready';
      /** What the filter applies: explicit ?? the file's declaration ?? 0 (ADR-030 §13, ruled). */
      degrees: number;
      /** The arm-space the file declared on arrival, or null. */
      file: number | null;
      /** True once an armSpace.set has committed. */
      explicit: boolean;
    }
  | { kind: 'refused'; reason: string };

export type HeadSpaceUiState =
  | {
      kind: 'ready';
      /**
       * What the armature wears right now, as a TOTAL from the character's
       * authored rest (ADR-030 §14): explicit ?? (the file's declaration +
       * the run's surgery). The number survives export and reopen.
       */
      degrees: number;
      /**
       * The measured per-pair seating baseline the run found on the bind as
       * loaded, or null when no source pair could be measured — a target
       * with no clips, or an unmeasurable source.
       */
      baseline: number | null;
      /** True once a headSpace.set has committed — the baseline is overridden. */
      explicit: boolean;
      /** What the file declared on arrival, or null for a file that never passed through a seating door. */
      file: HeadSeatingMarker | null;
    }
  | { kind: 'refused'; reason: string };

export interface CharacterPanelDeps {
  /** Elbow pin controls, mounted by the app. */
  readonly mountElbows?: (container: HTMLElement) => () => void;
  /** Capability + current value, asked at render time (the subject is replaceable). */
  readonly armSpace: () => ArmSpaceUiState;
  /** Commit through the command log — the panel never writes the Document. */
  readonly setArmSpace: (degrees: number) => void;
  /** Transient display-only preview while the thumb is down — null clears it.
   *  Never touches the Document; the commit above is the only durable write. */
  readonly previewArmSpace: (degrees: number | null) => void;
  readonly headSpace: () => HeadSpaceUiState;
  readonly setHeadSpace: (degrees: number) => void;
  readonly previewHeadSpace: (degrees: number | null) => void;
}

export function createCharacterPanel(deps: CharacterPanelDeps): PanelMount {
  return (container, store) => {
    const section = document.createElement('section');
    section.className = 'character-panel';

    const arm = createValueRow({
      label: 'Arm-space',
      unit: '°',
      min: -ARM_SPACE_MAX_DEGREES,
      max: ARM_SPACE_MAX_DEGREES,
      step: 1,
      precision: 0,
      tip: 'Constant shoulder abduction at play, all clips',
      describe: () => {
        const s = deps.armSpace();
        const arrived =
          s.kind === 'ready' && s.file !== null
            ? ` This character arrived declaring **${s.file >= 0 ? '+' : ''}${s.file.toFixed(0)}°**; that is what plays until you set it.`
            : '';
        return (
          'Constant shoulder abduction about the torso, applied at play over every clip — the Mixamo arm-space idea, as a ' +
          'character property. The arm rest is never touched: the exported character DECLARES the value on its upper-arm nodes; ' +
          'World applies it every frame, anything that does not read it plays the authored rest, and delivered clips never carry it.' +
          arrived
        );
      },
      state: () => {
        const s = deps.armSpace();
        return s.kind === 'refused'
          ? { kind: 'refused', reason: s.reason }
          : { kind: 'ready', value: s.degrees, chip: s.explicit ? 'set' : s.file !== null ? 'in file' : null };
      },
      commit: (degrees) => {
        deps.setArmSpace(degrees);
      },
      preview: (degrees) => {
        deps.previewArmSpace(degrees);
      },
    });

    const head = createValueRow({
      label: 'Head-space',
      unit: '°',
      min: -HEAD_SPACE_MAX_DEGREES,
      max: HEAD_SPACE_MAX_DEGREES,
      step: 0.5,
      precision: 1,
      tip: 'Cervical pitch, all clips — + is erect',
      // Read at open time: the baseline is a live measurement of the current
      // source–character pair, and a stale number in an explainer is a lie.
      describe: () => {
        const s = deps.headSpace();
        if (s.kind === 'refused') return 'Head-space is unavailable on this rig.';
        const signed = (d: number): string => `${d >= 0 ? '+' : ''}${d.toFixed(1)}°`;
        const measured =
          s.baseline === null
            ? 'no source–character pair to measure a baseline against'
            : `measured seating baseline **${signed(s.baseline)}** for this source–character pair`;
        // A file that arrived seated says so: the value is the TOTAL from the
        // authored rest, and a hand-set declaration stands down the baseline
        // on reopen — the number a person saved is the number they see back.
        const arrived =
          s.file === null
            ? ''
            : s.file.provenance === 'explicit'
              ? `This character arrived seated **${signed(s.file.degrees)}**, set by hand in an earlier session — that declaration stands and the measured baseline stands down. `
              : `This character arrived seated **${signed(s.file.degrees)}** by an earlier session's measured baseline. `;
        const resting = s.explicit
          ? `Set by hand (${measured}). `
          : s.file?.provenance === 'explicit'
            ? `(${measured}, not applied.) `
            : `Resting at the ${measured}. `;
        return (
          `Cervical pitch over every clip — + is erect; the value is the total from the character's authored rest. ` +
          arrived +
          resting +
          `Seated into the armature: the exported character carries it in its bind, so every clip that drives the neck — ` +
          `here or in World — reads corrected, while delivered clips stay generic. A clip that leaves the neck at rest ` +
          `shows the authored head.`
        );
      },
      state: () => {
        const s = deps.headSpace();
        return s.kind === 'refused'
          ? { kind: 'refused', reason: s.reason }
          : {
              kind: 'ready',
              value: s.degrees,
              chip: s.explicit ? 'set' : s.file?.provenance === 'explicit' ? 'in file' : 'baseline',
            };
      },
      commit: (degrees) => {
        deps.setHeadSpace(degrees);
      },
      preview: (degrees) => {
        deps.previewHeadSpace(degrees);
      },
    });

    const advanced=document.createElement('div');advanced.className='body-advanced';
    advanced.append(arm.root,arm.refusal,head.root,head.refusal);
    container.append(section);
    const disposeElbows = deps.mountElbows?.(section);
    section.append(advanced);

    const render = (): void => {
      arm.render();
      head.render();
    };
    const unsubscribe = store.subscribe(render);
    render();

    return {
      dispose(): void {
        unsubscribe();
        disposeElbows?.();
        section.remove();
      },
    };
  };
}
